import { MakeLogicType, actions, connect, defaults, kea, key, listeners, path, props, reducers, selectors } from 'kea'

import {
    breakdownFilterToTaxonomicFilterType,
    propertyFilterTypeToPropertyDefinitionType,
    taxonomicFilterTypeToPropertyFilterType,
} from 'lib/components/PropertyFilters/utils'
import {
    TaxonomicFilterGroup,
    TaxonomicFilterGroupType,
    TaxonomicFilterValue,
} from 'lib/components/TaxonomicFilter/types'
import { featureFlagLogic } from 'lib/logic/featureFlagLogic'
import { insightVizDataLogic } from 'scenes/insights/insightVizDataLogic'
import { keyForInsightLogicProps } from 'scenes/insights/sharedUtils'

import { propertyDefinitionsModel } from '~/models/propertyDefinitionsModel'
import { Breakdown, BreakdownFilter } from '~/queries/schema/schema-general'
import { BreakdownType, ChartDisplayType, InsightLogicProps } from '~/types'

import type { FeatureFlagsSet } from '../../../../lib/logic/featureFlagLogic'
import type { DatabaseSchemaField } from '../../../../queries/schema/schema-general'
import type { PropertyDefinition, PropertyDefinitionType } from '../../../../types'
import { isCohortBreakdown, isMultipleBreakdownType, isURLNormalizeable } from './taxonomicBreakdownFilterUtils'

// Kept in sync with the `@maxItems` on `BreakdownFilter.breakdowns` in schema-general.ts.
const MAX_TRENDS_BREAKDOWNS = 3

export type TaxonomicBreakdownFilterLogicProps = {
    insightProps: InsightLogicProps
    breakdownFilter: BreakdownFilter
    display?: ChartDisplayType | null
    isTrends: boolean
    isFunnels: boolean
    updateBreakdownFilter: ((breakdownFilter: BreakdownFilter) => void) | null
    updateDisplay: ((display: ChartDisplayType | undefined) => void) | null
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface taxonomicBreakdownFilterLogicValues {
    featureFlags: FeatureFlagsSet // featureFlagLogic
    currentDataWarehouseSchemaColumns: DatabaseSchemaField[] // insightVizDataLogic
    hasDataWarehouseSeries: boolean // insightVizDataLogic
    isSingleSeriesDefinition: boolean // insightVizDataLogic
    getPropertyDefinition: (
        s: TaxonomicFilterValue,
        type: PropertyDefinitionType,
        groupTypeIndex?: number
    ) => PropertyDefinition | null // propertyDefinitionsModel
    __ignore: TaxonomicFilterGroupType | null
    addBreakdownDisabledReason: string | null
    breakdownArray: Breakdown[] | (number | string)[]
    breakdownCohortArray: (number | string | Breakdown)[]
    breakdownFilter: BreakdownFilter
    breakdownHideOtherAggregation: boolean | null | undefined
    breakdownLimit: number
    breakdownOptionsOpened: boolean
    hasGlobalBreakdownOptions: boolean
    histogramBinCount: number | undefined
    histogramBinsUsed: boolean
    includeSessions: boolean
    isMultipleBreakdownsEnabled: boolean
    localBreakdownHideOtherAggregation: boolean | undefined
    localBreakdownLimit: number | undefined
    localHistogramBinCount: number | undefined
    localNormalizeBreakdownURL: boolean | undefined
    localPathCleaningEnabled: boolean | undefined
    normalizeBreakdownUrl: boolean
    pathCleaningEnabled: boolean
    taxonomicBreakdownType:
        | TaxonomicFilterGroupType.Metadata
        | TaxonomicFilterGroupType.Actions
        | TaxonomicFilterGroupType.CohortsWithAllUsers
        | TaxonomicFilterGroupType.DataWarehouse
        | TaxonomicFilterGroupType.DataWarehouseSourceTables
        | TaxonomicFilterGroupType.DataWarehouseProperties
        | TaxonomicFilterGroupType.DataWarehousePersonProperties
        | TaxonomicFilterGroupType.Elements
        | TaxonomicFilterGroupType.Events
        | TaxonomicFilterGroupType.InternalEvents
        | TaxonomicFilterGroupType.InternalEventProperties
        | TaxonomicFilterGroupType.EventProperties
        | TaxonomicFilterGroupType.EventFeatureFlags
        | TaxonomicFilterGroupType.EventMetadata
        | TaxonomicFilterGroupType.NumericalEventProperties
        | TaxonomicFilterGroupType.PersonProperties
        | TaxonomicFilterGroupType.PersonMetadata
        | TaxonomicFilterGroupType.PageviewUrls
        | TaxonomicFilterGroupType.PageviewEvents
        | TaxonomicFilterGroupType.Screens
        | TaxonomicFilterGroupType.ScreenEvents
        | TaxonomicFilterGroupType.EmailAddresses
        | TaxonomicFilterGroupType.AutocaptureEvents
        | TaxonomicFilterGroupType.CustomEvents
        | TaxonomicFilterGroupType.Wildcards
        | TaxonomicFilterGroupType.GroupsPrefix
        | TaxonomicFilterGroupType.Persons
        | TaxonomicFilterGroupType.FeatureFlags
        | TaxonomicFilterGroupType.Insights
        | TaxonomicFilterGroupType.Experiments
        | TaxonomicFilterGroupType.Plugins
        | TaxonomicFilterGroupType.Dashboards
        | TaxonomicFilterGroupType.GroupNamesPrefix
        | TaxonomicFilterGroupType.SessionProperties
        | TaxonomicFilterGroupType.HogQLExpression
        | TaxonomicFilterGroupType.Notebooks
        | TaxonomicFilterGroupType.LogEntries
        | TaxonomicFilterGroupType.ErrorTrackingIssues
        | TaxonomicFilterGroupType.Logs
        | TaxonomicFilterGroupType.LogAttributes
        | TaxonomicFilterGroupType.LogResourceAttributes
        | TaxonomicFilterGroupType.MetricAttributes
        | TaxonomicFilterGroupType.Spans
        | TaxonomicFilterGroupType.SpanAttributes
        | TaxonomicFilterGroupType.SpanResourceAttributes
        | TaxonomicFilterGroupType.Replay
        | TaxonomicFilterGroupType.ReplaySavedFilters
        | TaxonomicFilterGroupType.RevenueAnalyticsProperties
        | TaxonomicFilterGroupType.AccountCustomProperties
        | TaxonomicFilterGroupType.Resources
        | TaxonomicFilterGroupType.ErrorTrackingProperties
        | TaxonomicFilterGroupType.ActivityLogProperties
        | TaxonomicFilterGroupType.MCPProperties
        | TaxonomicFilterGroupType.MaxAIContext
        | TaxonomicFilterGroupType.WorkflowVariables
        | TaxonomicFilterGroupType.SuggestedFilters
        | TaxonomicFilterGroupType.RecentFilters
        | TaxonomicFilterGroupType.PinnedFilters
        | TaxonomicFilterGroupType.Empty
        | undefined
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface taxonomicBreakdownFilterLogicActions {
    addBreakdown: (
        breakdown: TaxonomicFilterValue,
        taxonomicGroup: TaxonomicFilterGroup
    ) => {
        breakdown: TaxonomicFilterValue
        taxonomicGroup: TaxonomicFilterGroup
    }
    removeBreakdown: (
        breakdown: number | string,
        breakdownType: string
    ) => {
        breakdown: number | string
        breakdownType: string
    }
    replaceBreakdown: (
        previousBreakdown: {
            type: string
            value: number | string
        },
        newBreakdown: {
            group: TaxonomicFilterGroup
            value: TaxonomicFilterValue
        }
    ) => {
        newBreakdown: {
            group: TaxonomicFilterGroup
            value: TaxonomicFilterValue
        }
        previousBreakdown: {
            type: string
            value: number | string
        }
    }
    setBreakdownHideOtherAggregation: (hidden: boolean) => {
        hidden: boolean
    }
    setBreakdownLimit: (value: number | undefined) => {
        value: number | undefined
    }
    setHistogramBinCount: (
        breakdown: number | string,
        breakdownType: string,
        count: number | undefined
    ) => {
        breakdown: number | string
        breakdownType: string
        count: number | undefined
    }
    setHistogramBinsUsed: (
        breakdown: number | string,
        breakdownType: string,
        binsUsed: boolean,
        binCount?: number
    ) => {
        binCount: number | undefined
        binsUsed: boolean
        breakdown: number | string
        breakdownType: string
    }
    setNormalizeBreakdownURL: (
        breakdown: number | string,
        breakdownType: string,
        normalizeBreakdownURL: boolean
    ) => {
        breakdown: number | string
        breakdownType: string
        normalizeBreakdownURL: boolean
    }
    setPathCleaningEnabled: (
        breakdown: number | string,
        breakdownType: string,
        pathCleaningEnabled: boolean
    ) => {
        breakdown: number | string
        breakdownType: string
        pathCleaningEnabled: boolean
    }
    toggleBreakdownOptions: (opened: boolean) => {
        opened: boolean
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface taxonomicBreakdownFilterLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        isMultipleBreakdownsEnabled: (isTrends: boolean) => boolean
        breakdownFilter: (breakdownFilter: BreakdownFilter) => BreakdownFilter
        includeSessions: (isTrends: boolean) => boolean
        addBreakdownDisabledReason: (
            breakdownFilter: BreakdownFilter,
            isMultipleBreakdownsEnabled: boolean,
            hasDataWarehouseSeries: boolean,
            isFunnels: boolean,
            isTrends: boolean,
            isSingleSeriesDefinition: boolean
        ) => string | null
        taxonomicBreakdownType: (
            breakdownFilter: BreakdownFilter
        ) =>
            | TaxonomicFilterGroupType.Metadata
            | TaxonomicFilterGroupType.Actions
            | TaxonomicFilterGroupType.CohortsWithAllUsers
            | TaxonomicFilterGroupType.DataWarehouse
            | TaxonomicFilterGroupType.DataWarehouseSourceTables
            | TaxonomicFilterGroupType.DataWarehouseProperties
            | TaxonomicFilterGroupType.DataWarehousePersonProperties
            | TaxonomicFilterGroupType.Elements
            | TaxonomicFilterGroupType.Events
            | TaxonomicFilterGroupType.InternalEvents
            | TaxonomicFilterGroupType.InternalEventProperties
            | TaxonomicFilterGroupType.EventProperties
            | TaxonomicFilterGroupType.EventFeatureFlags
            | TaxonomicFilterGroupType.EventMetadata
            | TaxonomicFilterGroupType.NumericalEventProperties
            | TaxonomicFilterGroupType.PersonProperties
            | TaxonomicFilterGroupType.PersonMetadata
            | TaxonomicFilterGroupType.PageviewUrls
            | TaxonomicFilterGroupType.PageviewEvents
            | TaxonomicFilterGroupType.Screens
            | TaxonomicFilterGroupType.ScreenEvents
            | TaxonomicFilterGroupType.EmailAddresses
            | TaxonomicFilterGroupType.AutocaptureEvents
            | TaxonomicFilterGroupType.CustomEvents
            | TaxonomicFilterGroupType.Wildcards
            | TaxonomicFilterGroupType.GroupsPrefix
            | TaxonomicFilterGroupType.Persons
            | TaxonomicFilterGroupType.FeatureFlags
            | TaxonomicFilterGroupType.Insights
            | TaxonomicFilterGroupType.Experiments
            | TaxonomicFilterGroupType.Plugins
            | TaxonomicFilterGroupType.Dashboards
            | TaxonomicFilterGroupType.GroupNamesPrefix
            | TaxonomicFilterGroupType.SessionProperties
            | TaxonomicFilterGroupType.HogQLExpression
            | TaxonomicFilterGroupType.Notebooks
            | TaxonomicFilterGroupType.LogEntries
            | TaxonomicFilterGroupType.ErrorTrackingIssues
            | TaxonomicFilterGroupType.Logs
            | TaxonomicFilterGroupType.LogAttributes
            | TaxonomicFilterGroupType.LogResourceAttributes
            | TaxonomicFilterGroupType.MetricAttributes
            | TaxonomicFilterGroupType.Spans
            | TaxonomicFilterGroupType.SpanAttributes
            | TaxonomicFilterGroupType.SpanResourceAttributes
            | TaxonomicFilterGroupType.Replay
            | TaxonomicFilterGroupType.ReplaySavedFilters
            | TaxonomicFilterGroupType.RevenueAnalyticsProperties
            | TaxonomicFilterGroupType.AccountCustomProperties
            | TaxonomicFilterGroupType.Resources
            | TaxonomicFilterGroupType.ErrorTrackingProperties
            | TaxonomicFilterGroupType.ActivityLogProperties
            | TaxonomicFilterGroupType.MCPProperties
            | TaxonomicFilterGroupType.MaxAIContext
            | TaxonomicFilterGroupType.WorkflowVariables
            | TaxonomicFilterGroupType.SuggestedFilters
            | TaxonomicFilterGroupType.RecentFilters
            | TaxonomicFilterGroupType.PinnedFilters
            | TaxonomicFilterGroupType.Empty
            | undefined
        breakdownArray: (
            breakdownFilter: BreakdownFilter,
            isMultipleBreakdownsEnabled: boolean
        ) => Breakdown[] | (number | string)[]
        breakdownCohortArray: (breakdownArray: Breakdown[] | (number | string)[]) => (number | string | Breakdown)[]
        histogramBinsUsed: (breakdownFilter: BreakdownFilter) => boolean
        histogramBinCount: (
            breakdownFilter: BreakdownFilter,
            localHistogramBinCount: number | undefined
        ) => number | undefined
        breakdownLimit: (breakdownFilter: BreakdownFilter, localBreakdownLimit: number | undefined) => number
        hasGlobalBreakdownOptions: (breakdownFilter: BreakdownFilter) => boolean
        normalizeBreakdownUrl: (
            breakdownFilter: BreakdownFilter,
            localNormalizeBreakdownURL: boolean | undefined
        ) => boolean
        pathCleaningEnabled: (
            breakdownFilter: BreakdownFilter,
            localPathCleaningEnabled: boolean | undefined
        ) => boolean
        breakdownHideOtherAggregation: (
            breakdownFilter: BreakdownFilter,
            localBreakdownHideOtherAggregation: boolean | undefined
        ) => boolean | null | undefined
    }
}

export type taxonomicBreakdownFilterLogicType = MakeLogicType<
    taxonomicBreakdownFilterLogicValues,
    taxonomicBreakdownFilterLogicActions,
    TaxonomicBreakdownFilterLogicProps,
    taxonomicBreakdownFilterLogicMeta
>

export const taxonomicBreakdownFilterLogic = kea<taxonomicBreakdownFilterLogicType>([
    props({} as TaxonomicBreakdownFilterLogicProps),
    key((props) => keyForInsightLogicProps('new')(props.insightProps)),
    path(['scenes', 'insights', 'filters', 'BreakdownFilter', 'taxonomicBreakdownFilterLogic']),
    defaults({
        // This is a hack to get `TaxonomicFilterGroupType` imported in `taxonomicBreakdownFilterLogicType.ts`
        __ignore: null as TaxonomicFilterGroupType | null,
    }),
    connect((props: TaxonomicBreakdownFilterLogicProps) => ({
        values: [
            insightVizDataLogic(props.insightProps),
            ['currentDataWarehouseSchemaColumns', 'hasDataWarehouseSeries', 'isSingleSeriesDefinition'],
            propertyDefinitionsModel,
            ['getPropertyDefinition'],
            featureFlagLogic,
            ['featureFlags'],
        ],
    })),
    actions({
        addBreakdown: (breakdown: TaxonomicFilterValue, taxonomicGroup: TaxonomicFilterGroup) => ({
            breakdown,
            taxonomicGroup,
        }),
        replaceBreakdown: (
            previousBreakdown: {
                value: string | number
                type: string
            },
            newBreakdown: {
                value: TaxonomicFilterValue
                group: TaxonomicFilterGroup
            }
        ) => ({
            previousBreakdown,
            newBreakdown,
        }),
        removeBreakdown: (breakdown: string | number, breakdownType: string) => ({ breakdown, breakdownType }),
        setBreakdownLimit: (value: number | undefined) => ({ value }),
        setHistogramBinsUsed: (
            breakdown: string | number,
            breakdownType: string,
            binsUsed: boolean,
            binCount?: number
        ) => ({
            binsUsed,
            binCount,
            breakdown,
            breakdownType,
        }),
        setHistogramBinCount: (breakdown: string | number, breakdownType: string, count: number | undefined) => ({
            breakdown,
            breakdownType,
            count,
        }),
        setNormalizeBreakdownURL: (
            breakdown: string | number,
            breakdownType: string,
            normalizeBreakdownURL: boolean
        ) => ({
            breakdown,
            breakdownType,
            normalizeBreakdownURL,
        }),
        setPathCleaningEnabled: (breakdown: string | number, breakdownType: string, pathCleaningEnabled: boolean) => ({
            breakdown,
            breakdownType,
            pathCleaningEnabled,
        }),
        toggleBreakdownOptions: (opened: boolean) => ({
            opened,
        }),
        setBreakdownHideOtherAggregation: (hidden: boolean) => ({
            hidden,
        }),
    }),
    reducers({
        localHistogramBinCount: [
            10 as number | undefined,
            {
                setHistogramBinCount: (_, { count }) => count,
            },
        ],
        localBreakdownLimit: [
            undefined as number | undefined,
            {
                setBreakdownLimit: (_, { value }) => value,
            },
        ],
        localNormalizeBreakdownURL: [
            undefined as boolean | undefined,
            {
                setNormalizeBreakdownURL: (_, { normalizeBreakdownURL }) => normalizeBreakdownURL,
            },
        ],
        localPathCleaningEnabled: [
            undefined as boolean | undefined,
            {
                setPathCleaningEnabled: (_, { pathCleaningEnabled }) => pathCleaningEnabled,
            },
        ],
        localBreakdownHideOtherAggregation: [
            undefined as boolean | undefined,
            {
                setBreakdownHideOtherAggregation: (_, { hidden }) => hidden,
            },
        ],
        breakdownOptionsOpened: [
            false as boolean,
            {
                toggleBreakdownOptions: (_, { opened }) => opened,
            },
        ],
    }),
    selectors({
        isMultipleBreakdownsEnabled: [(_, p) => [p.isTrends], (isTrends: boolean) => isTrends],
        breakdownFilter: [(_, p) => [p.breakdownFilter], (breakdownFilter: BreakdownFilter) => breakdownFilter],
        includeSessions: [(_, p) => [p.isTrends], (isTrends: boolean) => isTrends],
        addBreakdownDisabledReason: [
            (s, p) => [
                s.breakdownFilter,
                s.isMultipleBreakdownsEnabled,
                s.hasDataWarehouseSeries,
                p.isFunnels,
                p.isTrends,
                s.isSingleSeriesDefinition,
            ],
            (
                { breakdown, breakdowns, breakdown_type },
                isMultipleBreakdownsEnabled: boolean,
                hasDataWarehouseSeries: boolean,
                isFunnels: boolean,
                isTrends: boolean,
                isSingleSeriesDefinition: boolean
            ): string | null => {
                if (isMultipleBreakdownsEnabled && (!breakdown_type || isMultipleBreakdownType(breakdown_type))) {
                    if (!!breakdowns && breakdowns.length >= MAX_TRENDS_BREAKDOWNS) {
                        return `You can break down trends by up to ${MAX_TRENDS_BREAKDOWNS} properties. Remove one to add another.`
                    }
                    return null
                }

                // Funnels only supports a single cohort breakdown
                const hasFunnelCohortBreakdown =
                    isFunnels && breakdown_type === 'cohort' && Array.isArray(breakdown) && breakdown.length >= 1
                if (hasFunnelCohortBreakdown) {
                    return 'Funnels support a single cohort breakdown. Remove the existing one to change it.'
                }

                if (isTrends && !isSingleSeriesDefinition && hasDataWarehouseSeries) {
                    return 'Breakdowns are not supported for multiple series types when at least one of them is a data warehouse series.'
                }

                if (!Array.isArray(breakdown) && breakdown != null) {
                    return 'This insight type supports a single breakdown. Remove the existing one to change it.'
                }

                return null
            },
        ],
        taxonomicBreakdownType: [
            (s) => [s.breakdownFilter],
            (breakdownFilter: BreakdownFilter) => {
                let breakdownType = breakdownFilterToTaxonomicFilterType(breakdownFilter)
                if (breakdownType === TaxonomicFilterGroupType.Cohorts) {
                    breakdownType = TaxonomicFilterGroupType.CohortsWithAllUsers
                }
                return breakdownType
            },
        ],
        breakdownArray: [
            (s) => [s.breakdownFilter, s.isMultipleBreakdownsEnabled],
            ({ breakdown, breakdowns }, isMultipleBreakdownsEnabled: boolean): (string | number)[] | Breakdown[] => {
                if (isMultipleBreakdownsEnabled && breakdowns) {
                    return breakdowns
                }

                return (Array.isArray(breakdown) ? breakdown : [breakdown]).filter((b): b is string | number => !!b)
            },
        ],
        breakdownCohortArray: [
            (s) => [s.breakdownArray],
            (breakdownArray: Breakdown[] | (number | string)[]) =>
                breakdownArray.map((b) => (isNaN(Number(b)) ? b : Number(b))),
        ],
        histogramBinsUsed: [
            (s) => [s.breakdownFilter],
            ({ breakdown_histogram_bin_count }) => breakdown_histogram_bin_count !== undefined,
        ],
        histogramBinCount: [
            (s) => [s.breakdownFilter, s.localHistogramBinCount],
            (breakdownFilter: BreakdownFilter, localHistogramBinCount: number | undefined) =>
                localHistogramBinCount || breakdownFilter?.breakdown_histogram_bin_count,
        ],
        breakdownLimit: [
            (s) => [s.breakdownFilter, s.localBreakdownLimit],
            (breakdownFilter: BreakdownFilter, localBreakdownLimit: number | undefined) =>
                localBreakdownLimit || breakdownFilter?.breakdown_limit || 25,
        ],
        // Cohort breakdowns are over a user-picked set, so there's nothing to truncate
        // and no long-tail to bucket as "Other". The global options panel is empty in that case.
        hasGlobalBreakdownOptions: [
            (s) => [s.breakdownFilter],
            (breakdownFilter: BreakdownFilter) => breakdownFilter?.breakdown_type !== 'cohort',
        ],
        normalizeBreakdownUrl: [
            (s) => [s.breakdownFilter, s.localNormalizeBreakdownURL],
            (breakdownFilter: BreakdownFilter, localNormalizeBreakdownURL: boolean | undefined) =>
                localNormalizeBreakdownURL ?? breakdownFilter.breakdown_normalize_url ?? true,
        ],
        pathCleaningEnabled: [
            (s) => [s.breakdownFilter, s.localPathCleaningEnabled],
            (breakdownFilter: BreakdownFilter, localPathCleaningEnabled: boolean | undefined) =>
                localPathCleaningEnabled ?? breakdownFilter.breakdown_path_cleaning ?? false,
        ],
        breakdownHideOtherAggregation: [
            (s) => [s.breakdownFilter, s.localBreakdownHideOtherAggregation],
            (breakdownFilter: BreakdownFilter, localBreakdownHideOtherAggregation: boolean | undefined) =>
                localBreakdownHideOtherAggregation ?? breakdownFilter.breakdown_hide_other_aggregation,
        ],
    }),
    listeners(({ props, values, actions }) => ({
        addBreakdown: ({ breakdown, taxonomicGroup }) => {
            const breakdownType = taxonomicFilterTypeToPropertyFilterType(taxonomicGroup.type) as BreakdownType
            const propertyDefinitionType = propertyFilterTypeToPropertyDefinitionType(breakdownType)
            const isHistogramable =
                !!values.getPropertyDefinition(breakdown, propertyDefinitionType)?.is_numerical && props.isTrends

            if (!props.updateBreakdownFilter || !breakdownType) {
                return
            }

            // If property definitions are not loaded when this runs then a normalizeable URL will not be normalized.
            // For now, it is safe to fall back to `breakdown` instead of the property definition.
            const isNormalizeable = isURLNormalizeable(
                values.getPropertyDefinition(breakdown, propertyDefinitionType)?.name || (breakdown as string)
            )

            const { breakdownFilter } = values

            // TODO: We're preventing duplicated cohorts with a Set. A better fix would be
            // to make excludedProperties work for cohorts in the TaxonomicFilter.
            const cohortBreakdown =
                breakdownFilter?.breakdown_type === 'cohort'
                    ? (Array.from(new Set([...values.breakdownCohortArray, breakdown])) as (string | number)[])
                    : ([breakdown] as (string | number)[])

            if (values.isMultipleBreakdownsEnabled && isMultipleBreakdownType(breakdownType)) {
                if (
                    checkBreakdownExists(breakdownFilter.breakdowns, breakdown, breakdownType) ||
                    (breakdownFilter.breakdown === breakdown && breakdownType === breakdownFilter.breakdown_type)
                ) {
                    return
                }

                const newBreakdown: Breakdown = {
                    property: breakdown as string,
                    type: breakdownType,
                    group_type_index: taxonomicGroup.groupTypeIndex,
                    histogram_bin_count: isHistogramable ? 10 : undefined,
                    normalize_url: isNormalizeable ? true : undefined,
                }

                const breakdowns = breakdownFilter.breakdowns
                    ? [...breakdownFilter.breakdowns, newBreakdown]
                    : [newBreakdown]

                // Keep backwards compatibility with old breakdowns. If there is a breakdown, convert it first to multiple breakdowns.
                if (isSingleBreakdown(breakdownFilter) && isMultipleBreakdownType(breakdownFilter.breakdown_type)) {
                    props.updateBreakdownFilter({
                        breakdown: undefined,
                        breakdown_type: undefined,
                        breakdown_histogram_bin_count: undefined,
                        breakdown_normalize_url: undefined,
                        breakdown_group_type_index: undefined,
                        breakdowns: [
                            {
                                property: breakdownFilter.breakdown as string,
                                type: breakdownFilter.breakdown_type,
                                group_type_index: breakdownFilter.breakdown_group_type_index,
                                histogram_bin_count: breakdownFilter.breakdown_histogram_bin_count,
                                normalize_url: breakdownFilter.breakdown_normalize_url,
                            },
                            ...breakdowns,
                        ],
                    })
                } else {
                    props.updateBreakdownFilter({
                        breakdown: undefined,
                        breakdown_type: undefined,
                        breakdown_histogram_bin_count: undefined,
                        breakdown_normalize_url: undefined,
                        breakdown_group_type_index: undefined,
                        breakdowns,
                    })
                }

                // Make sure we are no longer in map view after removing the Country Code breakdown
                if (
                    props.isTrends &&
                    props.display === ChartDisplayType.WorldMap &&
                    (breakdowns.length !== 1 || breakdowns[0].property !== '$geoip_country_code')
                ) {
                    props.updateDisplay?.(undefined)
                }
            } else {
                props.updateBreakdownFilter({
                    breakdowns: undefined,
                    breakdown_type: breakdownType,
                    breakdown:
                        taxonomicGroup.type === TaxonomicFilterGroupType.CohortsWithAllUsers
                            ? cohortBreakdown
                            : breakdown,
                    breakdown_group_type_index: taxonomicGroup.groupTypeIndex,
                    breakdown_histogram_bin_count: isHistogramable ? 10 : undefined,
                    breakdown_normalize_url: isNormalizeable ? true : undefined,
                })
            }
        },
        removeBreakdown: ({ breakdown, breakdownType }) => {
            if (!props.updateBreakdownFilter) {
                return
            }

            if (isCohortBreakdown(breakdown)) {
                const newParts = values.breakdownCohortArray.filter(
                    (cohort): cohort is string | number => cohort !== breakdown && typeof cohort !== 'object'
                )

                if (newParts.length === 0) {
                    props.updateBreakdownFilter({ ...props.breakdownFilter, breakdown: null, breakdown_type: null })
                } else {
                    props.updateBreakdownFilter({
                        ...props.breakdownFilter,
                        breakdown: newParts,
                        breakdown_type: 'cohort',
                    })
                }
            } else if (values.isMultipleBreakdownsEnabled) {
                const breakdowns = props.breakdownFilter.breakdowns?.filter(
                    (savedBreakdown) =>
                        !(savedBreakdown.property === breakdown && savedBreakdown.type === breakdownType)
                )

                props.updateBreakdownFilter({
                    ...props.breakdownFilter,
                    breakdown: undefined,
                    breakdown_type: undefined,
                    breakdown_histogram_bin_count: undefined,
                    breakdowns: breakdowns && breakdowns.length === 0 ? undefined : breakdowns,
                })

                // Make sure we are no longer in map view after removing the Country Code breakdown
                if (
                    (!breakdowns || breakdowns.length === 0) &&
                    props.isTrends &&
                    props.display === ChartDisplayType.WorldMap
                ) {
                    props.updateDisplay?.(undefined)
                }
            } else {
                props.updateBreakdownFilter({
                    ...props.breakdownFilter,
                    breakdowns: undefined,
                    breakdown: undefined,
                    breakdown_type: undefined,
                    breakdown_histogram_bin_count: undefined,
                })

                // Make sure we are no longer in map view after removing the Country Code breakdown
                if (props.isTrends && props.display === ChartDisplayType.WorldMap) {
                    props.updateDisplay?.(undefined)
                }
            }
        },
        replaceBreakdown: ({ previousBreakdown, newBreakdown }) => {
            const breakdownType = taxonomicFilterTypeToPropertyFilterType(newBreakdown.group.type) as
                | BreakdownType
                | undefined
            const breakdownValue = newBreakdown.value

            const propertyDefinitionType = propertyFilterTypeToPropertyDefinitionType(breakdownType)
            const isHistogramable =
                !!values.getPropertyDefinition(breakdownValue, propertyDefinitionType)?.is_numerical && props.isTrends

            if (
                !props.updateBreakdownFilter ||
                !breakdownType ||
                (breakdownType === previousBreakdown.type && breakdownValue === previousBreakdown.value) ||
                checkBreakdownExists(values.breakdownFilter.breakdowns, breakdownValue, breakdownType)
            ) {
                return
            }

            // If property definitions are not loaded when this runs then a normalizeable URL will not be normalized.
            // For now, it is safe to fall back to `breakdown` instead of the property definition.
            const isNormalizeable = isURLNormalizeable(
                values.getPropertyDefinition(breakdownValue, propertyDefinitionType)?.name || (breakdownValue as string)
            )

            if (
                values.isMultipleBreakdownsEnabled &&
                isMultipleBreakdownType(breakdownType) &&
                typeof breakdownValue === 'string'
            ) {
                // Backward compatibility. If there is a single breakdown, remove it.
                if (!values.breakdownFilter.breakdowns) {
                    props.updateBreakdownFilter?.({
                        breakdowns: [
                            {
                                property: breakdownValue,
                                type: breakdownType,
                                group_type_index: newBreakdown.group.groupTypeIndex,
                                histogram_bin_count: isHistogramable ? 10 : undefined,
                                normalize_url: isNormalizeable ? true : undefined,
                            },
                        ],
                        breakdown: undefined,
                        breakdown_type: undefined,
                        breakdown_histogram_bin_count: undefined,
                        breakdown_normalize_url: undefined,
                        breakdown_group_type_index: undefined,
                    })
                } else {
                    const breakdowns = values.breakdownFilter.breakdowns?.map((savedBreakdown) => {
                        if (
                            savedBreakdown.property === previousBreakdown.value &&
                            savedBreakdown.type === previousBreakdown.type
                        ) {
                            return {
                                ...savedBreakdown,
                                property: breakdownValue,
                                type: breakdownType,
                                group_type_index: newBreakdown.group.groupTypeIndex,
                                histogram_bin_count: isHistogramable
                                    ? savedBreakdown.histogram_bin_count || 10
                                    : undefined,
                                normalize_url: isNormalizeable ? (savedBreakdown.normalize_url ?? true) : undefined,
                            }
                        }

                        return savedBreakdown
                    })

                    props.updateBreakdownFilter?.({
                        breakdowns,
                    })
                }
            } else if (breakdownType === 'cohort') {
                const newCohortBreakdown = values.breakdownCohortArray.map((cohort) =>
                    cohort === previousBreakdown.value ? breakdownValue : cohort
                ) as (string | number)[]
                props.updateBreakdownFilter({
                    breakdown: newCohortBreakdown,
                    breakdown_type: 'cohort',
                })
            } else {
                actions.addBreakdown(newBreakdown.value, newBreakdown.group)
            }
        },
        setBreakdownLimit: async ({ value }, breakpoint) => {
            await breakpoint(300)

            props.updateBreakdownFilter?.({
                ...values.breakdownFilter,
                breakdown_limit: value,
            })
        },
        setNormalizeBreakdownURL: ({ normalizeBreakdownURL, breakdown, breakdownType }) => {
            if (values.isMultipleBreakdownsEnabled && !isSingleBreakdown(values.breakdownFilter)) {
                props.updateBreakdownFilter?.({
                    breakdown_normalize_url: undefined,
                    breakdowns: updateNestedBreakdown(
                        values.breakdownFilter.breakdowns,
                        {
                            normalize_url: normalizeBreakdownURL,
                        },
                        breakdown,
                        breakdownType
                    ),
                })
            } else {
                props.updateBreakdownFilter?.({
                    breakdown_normalize_url: normalizeBreakdownURL,
                })
            }
        },
        setPathCleaningEnabled: ({ pathCleaningEnabled }) => {
            props.updateBreakdownFilter?.({
                breakdown_path_cleaning: pathCleaningEnabled,
            })
        },
        setHistogramBinsUsed: ({ binsUsed, binCount, breakdown, breakdownType }) => {
            if (values.isMultipleBreakdownsEnabled && !isSingleBreakdown(values.breakdownFilter)) {
                props.updateBreakdownFilter?.({
                    breakdown_histogram_bin_count: undefined,
                    breakdowns: updateNestedBreakdown(
                        values.breakdownFilter.breakdowns,
                        {
                            histogram_bin_count: binsUsed ? binCount : undefined,
                        },
                        breakdown,
                        breakdownType
                    ),
                })
            } else {
                props.updateBreakdownFilter?.({
                    breakdown_histogram_bin_count: binsUsed ? values.histogramBinCount : undefined,
                })
            }
        },
        setHistogramBinCount: async ({ count, breakdown, breakdownType }, breakpoint) => {
            await breakpoint(1000)

            if (values.isMultipleBreakdownsEnabled && !isSingleBreakdown(values.breakdownFilter)) {
                props.updateBreakdownFilter?.({
                    breakdown_histogram_bin_count: undefined,
                    breakdowns: updateNestedBreakdown(
                        values.breakdownFilter.breakdowns,
                        {
                            histogram_bin_count: count,
                        },
                        breakdown,
                        breakdownType
                    ),
                })
            } else {
                props.updateBreakdownFilter?.({
                    breakdown_histogram_bin_count: values.histogramBinsUsed ? count : undefined,
                })
            }
        },
        setBreakdownHideOtherAggregation: async ({ hidden }, breakpoint) => {
            await breakpoint(300)
            props.updateBreakdownFilter?.({
                breakdown_hide_other_aggregation: hidden,
            })
        },
    })),
])

function updateNestedBreakdown(
    breakdowns: Breakdown[] | undefined,
    breakdownUpdate: Partial<Breakdown>,
    lookupValue: string | number,
    lookupType: string
): Breakdown[] | undefined {
    return breakdowns?.map((savedBreakdown) =>
        savedBreakdown.property === lookupValue && savedBreakdown.type === lookupType
            ? {
                  ...savedBreakdown,
                  ...breakdownUpdate,
              }
            : savedBreakdown
    )
}

function checkBreakdownExists(
    breakdowns: Breakdown[] | undefined,
    lookupValue: string | number | null,
    lookupType: string
): boolean {
    return !!breakdowns?.find(
        (savedBreakdown) => savedBreakdown.property === lookupValue && savedBreakdown.type === lookupType
    )
}

export function isSingleBreakdown(breakdownFilter?: BreakdownFilter | null): boolean {
    return !!(breakdownFilter && breakdownFilter.breakdown_type && breakdownFilter.breakdown)
}
